import React, { useEffect, useState, useCallback } from "react";

import metadataReportDetailsApi from "apis/metadataReportDetails";
import metadataReportOverviewApi from "apis/metadataReportOverview";
import {
  Pagination,
  Select,
  SubHeader,
  Tab,
  Table,
} from "clearsense-ui/components";
import KpiCard from "components/Common/KpiCard";
import { stringifyFilters } from "components/DataConsumption/utils";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";
import convertToSelectOptions from "utils/convertToSelectOptions";

import {
  METADATA_DETAILS_COLUMN,
  METADATA_OVERVIEW_COLUMN,
  TABS,
} from "./constants";
import { convertBytes } from "./utils";

const MetadataReport = () => {
  const [activeTab, setActiveTab] = useState(TABS.overview);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isDomainsLoading, setIsDomainsLoading] = useState(false);
  const [isDataSourcesLoading, setIsDataSourcesLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [overviewData, setOverviewData] = useState([]);
  const [overviewCount, setOverviewCount] = useState();
  const [sortOverview, setSortOverview] = useState("");
  const [filters, setFilters] = useState({
    domain: null,
    data_source: null,
    subdomain: null,
    critical_data_element: null,
  });
  const [kpis, setKpis] = useState({
    domainKPI: 0,
    dataSourceKPI: 0,
    sizeKPI: 0,
  });
  const [detailsData, setDetailsData] = useState([]);
  const [sortDetails, setSortDetails] = useState("");

  const fetchOverviewData = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const currentPage = params.get("page") || 1;
    const pageSize = params.get("per_page") || 10;
    setIsTableLoading(true);
    try {
      const { data } = await metadataReportOverviewApi.fetch(
        currentPage,
        pageSize,
        sortOverview,
        stringifyFilters(filters)
      );
      setOverviewData(data.metadata);
      setOverviewCount(data.count);
      setKpis({
        domainKPI: data.domain_kpi,
        dataSourceKPI: data.data_source_kpi,
        sizeKPI: data.size_kpi,
      });
      const { data: detailsData } = await metadataReportDetailsApi.fetch(
        currentPage,
        pageSize,
        sortDetails,
        stringifyFilters(filters)
      );
      setDetailsData(detailsData.metadata);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsTableLoading(false);
    }
  }, [sortOverview, sortDetails, filters]);

  const { currentPage, pageSize, setCurrentPage, setPageSize } =
    useSearchAndPaginate(fetchOverviewData);

  useEffect(() => {
    const fetchDomains = async () => {
      setIsDomainsLoading(true);
      try {
        const { data } = await metadataReportOverviewApi.domains(
          stringifyFilters({ ...filters, domain: null })
        );
        setDomains(data?.domains);
      } catch (error) {
        logger.error(error);
      } finally {
        setIsDomainsLoading(false);
      }
    };

    const fetchDataSources = async () => {
      setIsDataSourcesLoading(true);
      try {
        const { data } = await metadataReportOverviewApi.data_sources(
          stringifyFilters({ ...filters, data_source: null })
        );
        setDataSources(data?.data_sources);
      } catch (error) {
        logger.error(error);
      } finally {
        setIsDataSourcesLoading(false);
      }
    };

    fetchDomains();
    fetchDataSources();
  }, [filters]);

  const handleTableChange = (sorter, callback) => {
    if (!sorter.order) {
      callback("");
    } else {
      callback(`${sorter.field} ${sorter.order === "ascend" ? "asc" : "desc"}`);
    }
  };

  const onFilterChange = (selectedOption, fieldDetails) => {
    setFilters({
      ...filters,
      [fieldDetails.name]: selectedOption?.value ?? selectedOption,
    });
  };

  return (
    <div className="px-8">
      <SubHeader
        breadcrumbs={[{ label: "Metadata Report", link: "#" }]}
        className="p-8 pl-0"
      />
      {activeTab === TABS.overview && (
        <div className="flex gap-10 justify-center mb-8">
          <KpiCard
            label="Domains"
            count={kpis.domainKPI}
            isLoading={isTableLoading}
          />
          <KpiCard
            label="Data Sources"
            count={kpis.dataSourceKPI}
            isLoading={isTableLoading}
          />
          <KpiCard label="Subdomains" count={0} isLoading={isTableLoading} />
          <KpiCard
            label="Critical Data Elements"
            count={0}
            isLoading={isTableLoading}
          />
          <KpiCard
            label="Terabytes"
            count={convertBytes(kpis.sizeKPI, "TB", 1)}
            isLoading={isTableLoading}
          />
        </div>
      )}
      <div className="flex gap-8">
        <Select
          isClearable
          label="Domain"
          name="domain"
          placeholder="Select a Domain"
          size="small"
          options={convertToSelectOptions(domains, { isSort: true })}
          isLoading={isDomainsLoading}
          onChange={(selectedOption, fieldDetails) =>
            onFilterChange(selectedOption, fieldDetails)
          }
          value={
            filters?.domain
              ? convertToSelectOptions([filters?.domain], { isSort: false })
              : null
          }
        />
        <Select
          isClearable
          label="Data Source"
          name="data_source"
          placeholder="Select a Data Source"
          size="small"
          options={convertToSelectOptions(dataSources, { isSort: true })}
          isLoading={isDataSourcesLoading}
          onChange={(selectedOption, fieldDetails) =>
            onFilterChange(selectedOption, fieldDetails)
          }
          value={
            filters?.data_source
              ? convertToSelectOptions([filters?.data_source], {
                  isSort: false,
                })
              : null
          }
        />
        <Select
          isClearable
          label="Subdomain"
          name="subdomain"
          placeholder="Select a Subdomain"
          size="small"
          onChange={(selectedOption, fieldDetails) =>
            onFilterChange(selectedOption, fieldDetails)
          }
          value={
            filters?.subdomain
              ? convertToSelectOptions([filters?.subdomain], { isSort: false })
              : null
          }
        />
        <Select
          isClearable
          label="Critical Data Element"
          name="critical_data_element"
          placeholder="Select a Critical Data Element"
          size="small"
          onChange={(selectedOption, fieldDetails) =>
            onFilterChange(selectedOption, fieldDetails)
          }
          value={
            filters?.critical_data_element
              ? convertToSelectOptions([filters?.critical_data_element], {
                  isSort: false,
                })
              : null
          }
        />
      </div>
      <Tab style="secondary" className="mb-4 mt-8">
        <Tab.Item
          active={activeTab === TABS.overview}
          onClick={() => setActiveTab(TABS.overview)}
        >
          Overview
        </Tab.Item>
        <Tab.Item
          active={activeTab === TABS.details}
          onClick={() => setActiveTab(TABS.details)}
        >
          Details
        </Tab.Item>
      </Tab>
      {activeTab === TABS.overview ? (
        <div className="h-[calc(100vh-552px)]">
          <Table
            fixedHeight
            isLoading={isTableLoading}
            rowData={overviewData}
            columnData={METADATA_OVERVIEW_COLUMN}
            onChange={(_pagination, _filters, sorter) =>
              handleTableChange(sorter, setSortOverview)
            }
          />
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRecordsCount={overviewCount}
            className="py-2 mt-auto"
            showResultsPerPage
          />
        </div>
      ) : (
        <div className="h-[calc(100vh-400px)]">
          <Table
            fixedHeight
            isLoading={isTableLoading}
            rowData={detailsData}
            columnData={METADATA_DETAILS_COLUMN}
            onChange={(_pagination, _filters, sorter) =>
              handleTableChange(sorter, setSortDetails)
            }
          />
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRecordsCount={overviewCount}
            className="py-2 mt-auto"
            showResultsPerPage
          />
        </div>
      )}
    </div>
  );
};

export default MetadataReport;
